// /settings/wepster — connect Wepster (token), point its webhook at us, pick
// the personal channel the portal writes first from, and switch client
// notifications on per event: plain text from here, or a Wepster chain with
// variables (temPlay `{{ amount }}`) so the wording is edited in Wepster.
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Copy, Loader2, MessagesSquare, RefreshCw, Save, Webhook } from "lucide-react";
import { MESSENGER_LABEL } from "../components/WepsterChat";

const errText = (e) => e?.response?.data?.detail || e?.message || "Request failed";
const PHONE_CHANNELS = ["telegram", "whatsapp", "viber", "alphasms", "echat"];

// Who writes from which number. A manager starting a chat (client card or an
// automatic notification) writes from their own account; nobody's → company default.
function AccountsCard() {
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [adding, setAdding] = useState({});
  const load = () => api.get("/wepster/accounts").then((r) => setData(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => {
    load();
    api.get("/employees/directory").then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => setStaff([]));
  }, []);
  const save = async (acc, staffIds) => {
    try {
      const r = await api.put(`/wepster/accounts/${acc.id}`, { staff_ids: staffIds });
      setData(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(errText(e));
    }
  };
  if (!data) return null;
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="pb-2"><CardTitle className="text-base text-zinc-100">Accounts → managers</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-zinc-500">
          Whose number is it. A manager starts new chats from their own account — from the client card and in
          automatic notifications (invoice → the deal's sales manager). Without an account of their own, the default
          below is used. Connect new accounts in Wepster → Project settings → Communication channels; they appear here.
        </p>
        {data.accounts.length === 0 && <p className="text-xs text-zinc-500">No accounts connected in Wepster.</p>}
        {data.accounts.map((acc) => (
          <div key={acc.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2">
            <span className="text-sm text-zinc-100 min-w-[200px]">
              {MESSENGER_LABEL[acc.type] || acc.type} · {acc.name}
              {acc.personal_account ? <span className="text-zinc-500"> · personal</span> : null}
              {!acc.can_write_first && <span className="text-amber-400 text-xs"> · replies only</span>}
            </span>
            {acc.staff_ids.map((sid, i) => (
              <span key={sid} className="text-xs px-2 py-0.5 rounded-md border border-sky-500/40 text-sky-300 bg-sky-500/10 flex items-center gap-1">
                {acc.staff_names[i]}
                <button className="hover:text-red-400" onClick={() => save(acc, acc.staff_ids.filter((x) => x !== sid))}>×</button>
              </span>
            ))}
            <select
              value={adding[acc.id] || ""}
              onChange={(e) => {
                const v = e.target.value;
                setAdding((a) => ({ ...a, [acc.id]: "" }));
                if (v) save(acc, [...acc.staff_ids, v]);
              }}
              className="bg-zinc-800 border border-zinc-700 text-zinc-300 h-7 rounded-md px-2 text-xs ml-auto"
            >
              <option value="">+ manager</option>
              {staff.filter((s) => !acc.staff_ids.includes(s.id)).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        ))}
        {data.disconnected?.length > 0 && (
          <p className="text-xs text-amber-400">
            {data.disconnected.length} assigned account(s) are no longer connected in Wepster: {data.disconnected.map((d) => d.name || d.id).join(", ")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function WepsterSettingsPage() {
  const [status, setStatus] = useState(null);
  const [tokenMasked, setTokenMasked] = useState("");
  const [token, setToken] = useState("");
  const [channels, setChannels] = useState(null);
  const [notify, setNotify] = useState(null);
  const [busy, setBusy] = useState("");

  const loadStatus = async () => {
    const [st, integ] = await Promise.all([
      api.get("/wepster/status").catch(() => ({ data: { configured: false } })),
      api.get("/settings/integrations").catch(() => ({ data: {} })),
    ]);
    setStatus(st.data);
    setTokenMasked(integ.data?.wepster_api_key_masked || "");
    if (st.data?.configured) {
      api.get("/wepster/channels").then((r) => setChannels(r.data?.channels || [])).catch((e) => {
        setChannels([]);
        toast.error(errText(e));
      });
    }
  };

  useEffect(() => {
    loadStatus();
    api.get("/wepster/notify-settings").then((r) => setNotify(r.data)).catch((e) => toast.error(errText(e)));
  }, []);

  const saveToken = async () => {
    if (!token.trim()) return;
    setBusy("token");
    try {
      await api.put("/settings/integrations", { wepster_api_key: token.trim() });
      setToken("");
      toast.success("Token saved");
      await loadStatus();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy("");
    }
  };

  const run = async (key, fn, okText) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(okText(r));
      await loadStatus();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy("");
    }
  };

  const setEvent = (key, patch) =>
    setNotify((n) => ({ ...n, events: { ...n.events, [key]: { ...n.events[key], ...patch } } }));

  const saveNotify = async () => {
    setBusy("notify");
    try {
      const r = await api.put("/wepster/notify-settings", notify);
      setNotify(r.data);
      toast.success("Notifications saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy("");
    }
  };

  const personal = (channels || []).filter((c) => PHONE_CHANNELS.includes(c.type));

  return (
    <div data-testid="wepster-settings-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
          <MessagesSquare className="h-6 w-6 text-sky-400" />Wepster — messengers
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Our Telegram / Viber / WhatsApp numbers connected in Wepster: client chats on the client card,
          messenger leads on /social-leads, and client notifications.
        </p>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2"><CardTitle className="text-base text-zinc-100">Connection</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">
                Project API token (Wepster → Project settings → API token)
              </Label>
              <Input value={token} onChange={(e) => setToken(e.target.value)} type="password"
                     placeholder={tokenMasked || "not set"}
                     className="bg-zinc-800 border-zinc-700 text-zinc-50 h-9 w-80" />
            </div>
            <Button onClick={saveToken} disabled={!token.trim() || busy === "token"} className="h-9">
              {busy === "token" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save
            </Button>
            <span className={cn("text-xs ml-2", status?.configured ? "text-emerald-400" : "text-zinc-500")}>
              {status?.configured ? "● connected" : "○ not connected"}
            </span>
          </div>

          {status?.configured && (
            <>
              <div className="text-xs text-zinc-400">
                {status.counts?.leads ?? 0} chats · {status.counts?.linked ?? 0} linked to clients · {status.counts?.messages ?? 0} messages.
                {" "}Last sync: {status.last_incremental_at ? new Date(status.last_incremental_at).toLocaleString("uk-UA") : "never"}
                {status.last_event_at && <> · last webhook: {new Date(status.last_event_at).toLocaleString("uk-UA")}</>}
                {status.last_summary?.errors?.length > 0 && (
                  <span className="text-amber-400"> · {status.last_summary.errors.length} errors</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={!!busy}
                        onClick={() => run("sync", () => api.post("/wepster/sync"), (r) => r.data.status === "already_running" ? "A sync is already running" : "Sync started — results appear here in a minute")}>
                  {busy === "sync" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}Sync now
                </Button>
                <Button size="sm" variant="outline" disabled={!!busy}
                        onClick={() => run("full", () => api.post("/wepster/sync", null, { params: { full: true } }), (r) => r.data.status === "already_running" ? "A sync is already running" : "Full sync started — can take several minutes")}>
                  {busy === "full" && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Full sync (all histories)
                </Button>
                <Button size="sm" variant="outline" disabled={!!busy}
                        onClick={() => run("hook", () => api.post("/wepster/webhook/register"), () => "Webhook set in Wepster")}>
                  {busy === "hook" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Webhook className="h-3.5 w-3.5 mr-1" />}
                  {status.webhook?.registered_at ? "Re-register webhook" : "Register webhook"}
                </Button>
              </div>
              {status.webhook_url && (
                <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                  <span className="truncate max-w-[520px]">Webhook: {status.webhook_url.replace(/\/webhook\/.+$/, "/webhook/••••")}</span>
                  <button className="hover:text-zinc-300" onClick={() => { navigator.clipboard.writeText(status.webhook_url); toast.success("Copied"); }}>
                    <Copy className="h-3 w-3" />
                  </button>
                  {status.webhook?.registered_at && <span>· registered {new Date(status.webhook.registered_at).toLocaleDateString("uk-UA")}</span>}
                </div>
              )}
            </>
          )}

          {channels && (
            <div className="pt-2 border-t border-zinc-800">
              <p className="text-[10px] uppercase text-zinc-500 mb-1">Connected channels</p>
              {channels.length === 0 ? <p className="text-xs text-zinc-500">None returned</p> : (
                <div className="flex flex-wrap gap-1.5">
                  {channels.map((c) => (
                    <span key={c.id} className="text-xs px-2 py-0.5 rounded-md border border-zinc-700 text-zinc-300">
                      {MESSENGER_LABEL[c.type] || c.type} · {c.name}{c.personal_account ? " · personal" : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {status?.configured && <AccountsCard />}

      {notify && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-base text-zinc-100">Client notifications</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-zinc-400">Company default — for managers without their own account</Label>
                <select
                  value={`${notify.default_channel_type}|${notify.default_connection_id}`}
                  onChange={(e) => {
                    const [t, id] = e.target.value.split("|");
                    setNotify((n) => ({ ...n, default_channel_type: t, default_connection_id: id }));
                  }}
                  className="bg-zinc-800 border border-zinc-700 text-zinc-50 h-9 rounded-md px-2 text-sm w-80"
                >
                  <option value={`${notify.default_channel_type}|`}>— not set —</option>
                  {personal.map((c) => (
                    <option key={c.id} value={`${c.type}|${c.id}`}>
                      {MESSENGER_LABEL[c.type] || c.type} · {c.name}
                    </option>
                  ))}
                  {notify.default_connection_id && !personal.some((c) => c.id === notify.default_connection_id) && (
                    <option value={`${notify.default_channel_type}|${notify.default_connection_id}`}>
                      {MESSENGER_LABEL[notify.default_channel_type]} · {notify.default_connection_id}
                    </option>
                  )}
                </select>
              </div>
            </div>
            <p className="text-xs text-zinc-500">
              A client with a linked chat gets the message in that chat. Each event is sent once per invoice / brief;
              failures are logged on the client card. Placeholders: <code>{"{client_name}"}</code> etc. In chain mode the same
              values reach the Wepster chain as <code>{"{{ amount }}"}</code>.
            </p>

            {Object.entries(notify.events).map(([key, ev]) => (
              <div key={key} className="rounded-lg border border-zinc-800 p-3 space-y-2">
                <div className="flex items-center gap-3">
                  <Switch checked={ev.enabled} onCheckedChange={(v) => setEvent(key, { enabled: v })} />
                  <span className="text-sm text-zinc-100">{ev.label}</span>
                  <div className="ml-auto flex gap-1">
                    {[["text", "Text from portal"], ["chain", "Wepster chain"]].map(([m, l]) => (
                      <button key={m} onClick={() => setEvent(key, { mode: m })}
                              className={cn("text-xs px-2 py-0.5 rounded-md border",
                                ev.mode === m ? "border-sky-500/50 text-sky-300 bg-sky-500/10" : "border-zinc-700 text-zinc-400")}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-zinc-500">Variables: {ev.vars.map((v) => `{${v}}`).join(" ")}</p>
                {ev.mode === "chain" ? (
                  <Input value={ev.chain} onChange={(e) => setEvent(key, { chain: e.target.value })}
                         placeholder="Chain name or id in Wepster"
                         className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm w-80" />
                ) : (
                  <div className="grid gap-2 md:grid-cols-2">
                    {["uk", "en"].map((lang) => (
                      <div key={lang} className="space-y-1">
                        <Label className="text-[10px] uppercase text-zinc-500">{lang === "uk" ? "Ukrainian" : "English (foreign clients)"}</Label>
                        <Textarea rows={3} value={ev.text?.[lang] || ""}
                                  onChange={(e) => setEvent(key, { text: { ...ev.text, [lang]: e.target.value } })}
                                  className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <Button onClick={saveNotify} disabled={busy === "notify"}>
              {busy === "notify" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save notifications
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
